import { useInfiniteQuery } from '@tanstack/react-query';
import type { Paginated } from '@relay-towing/api';
import { getList } from '../api/list';
import { useHybridAuth } from './useHybridAuth';
import { useUserCompanies } from './useUserCompanies';

export const PAGE_SIZE = 25;

/**
 * A list endpoint a page at a time, for the mobile shell's lists: every page
 * through getList, typed Paginated<Item>, for the selected company. `key`
 * names the list in the cache; the params are part of the key, so a new
 * search or view is a new list rather than a filter over the old one.
 * `pollEvery` asks again that often (ms) while the page is in view, where
 * the app's 30-second default is too slow for the list.
 * `staleTime: 0` asks again on every mount and every return to the page,
 * for a list that must not wait out the cache's five minutes.
 */
export function usePagedList<Item>(
  key: string,
  url: string,
  params: object,
  {
    enabled = true,
    staleTime,
    pollEvery,
  }: { enabled?: boolean; staleTime?: number; pollEvery?: number } = {}
) {
  const { getAccessTokenSilently, isAuthenticated } = useHybridAuth();
  const { selectedCompanyId } = useUserCompanies();

  const query = useInfiniteQuery({
    queryKey: [key, selectedCompanyId, params],
    queryFn: async ({ pageParam }): Promise<Paginated<Item>> =>
      getList<Item>(
        url,
        { ...params, pageNo: pageParam, pageSize: PAGE_SIZE },
        { token: await getAccessTokenSilently(), companyId: selectedCompanyId }
      ),
    initialPageParam: 1,
    getNextPageParam: (last) => last.meta.next ?? undefined,
    enabled: enabled && isAuthenticated && !!selectedCompanyId,
    ...(staleTime === undefined ? {} : { staleTime }),
    ...(pollEvery === undefined ? {} : { refetchInterval: pollEvery }),
  });

  return {
    items: query.data?.pages.flatMap((page) => page.data) ?? [],
    /** The first page's meta: the total, and `groups` for a grouped list. */
    meta: query.data?.pages[0]?.meta,
    isLoading: query.isPending,
    isError: query.isError,
    refetch: () => void query.refetch(),
    hasMore: query.hasNextPage,
    loadMore: () => void query.fetchNextPage(),
    isLoadingMore: query.isFetchingNextPage,
  };
}

export type PagedList<Item> = ReturnType<typeof usePagedList<Item>>;
